import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, History, FileText, Shield, ArrowLeftRight, Clock, 
  ChevronDown, ChevronRight, Trash2, Eye, Filter, Calendar,
  AlertTriangle, CheckCircle, TrendingUp, TrendingDown, Search,
  RefreshCw, Loader2, Award, Target, Scale, X, BarChart3, Download
} from 'lucide-react';

const ProtectionHistoryPage = () => {
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [analyses, setAnalyses] = useState([]);
  const [filteredAnalyses, setFilteredAnalyses] = useState([]);
  const [filter, setFilter] = useState('all'); // all, analysis, comparison
  const [expandedItem, setExpandedItem] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleting, setDeleting] = useState(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/contract-protection/history?limit=50');
      setAnalyses(response.data.analyses || []);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    let filtered = analyses;
    
    // Apply type filter
    if (filter === 'analysis') {
      filtered = filtered.filter(a => a.type !== 'comparison');
    } else if (filter === 'comparison') {
      filtered = filtered.filter(a => a.type === 'comparison');
    }
    
    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.contract_type?.toLowerCase().includes(query) ||
        a.user_role?.toLowerCase().includes(query) ||
        a.content_preview?.toLowerCase().includes(query) ||
        a.contract_a_preview?.toLowerCase().includes(query) ||
        a.contract_b_preview?.toLowerCase().includes(query)
      );
    }
    
    setFilteredAnalyses(filtered);
  }, [analyses, filter, searchQuery]);

  const deleteAnalysis = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/contract-protection/history/${id}`);
      setAnalyses(prev => prev.filter(a => a.id !== id));
      toast.success('Análisis eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setDeleting(null);
    }
  };

  const downloadPDF = async (id, type) => {
    setDownloading(id);
    try {
      const response = await api.get(`/contract-protection/report/${id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'comparison' 
        ? `comparacion_${id.slice(0, 8)}.pdf`
        : `analisis_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error al descargar el PDF');
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha desconocida';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays} días`;
    
    return date.toLocaleDateString('es-CL', { 
      day: 'numeric', 
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-emerald-400';
    if (score >= 50) return 'text-yellow-400';
    if (score >= 25) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 75) return 'bg-emerald-500/20 border-emerald-500/30';
    if (score >= 50) return 'bg-yellow-500/20 border-yellow-500/30';
    if (score >= 25) return 'bg-orange-500/20 border-orange-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  const getRiskLevelConfig = (level) => {
    const configs = {
      bajo: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle },
      moderado: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: AlertTriangle },
      alto: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertTriangle },
      crítico: { bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertTriangle }
    };
    return configs[level] || configs.moderado;
  };

  const groupByDate = (items) => {
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    
    items.forEach(item => {
      if (!item.created_at) {
        if (!groups['Sin fecha']) groups['Sin fecha'] = [];
        groups['Sin fecha'].push(item);
        return;
      }
      
      const date = new Date(item.created_at);
      if (isNaN(date.getTime())) {
        if (!groups['Sin fecha']) groups['Sin fecha'] = [];
        groups['Sin fecha'].push(item);
        return;
      }
      
      const dateStr = date.toDateString();
      let label;
      
      if (dateStr === today) {
        label = 'Hoy';
      } else if (dateStr === yesterday) {
        label = 'Ayer';
      } else {
        label = date.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });
      }
      
      if (!groups[label]) groups[label] = [];
      groups[label].push(item);
    });
    
    return groups;
  };

  const groupedAnalyses = groupByDate(filteredAnalyses);

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/dashboard" 
              className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="flex items-center space-x-3">
              <History className="w-6 h-6 text-amber-500" />
              <span className="font-semibold font-['Outfit']">Historial de Análisis</span>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/proteger-contrato"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
              >
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">Nuevo</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-white">{analyses.length}</div>
            <div className="text-sm text-slate-400">Total Análisis</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-purple-400">
              {analyses.filter(a => a.type === 'comparison').length}
            </div>
            <div className="text-sm text-slate-400">Comparaciones</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-emerald-400">
              {analyses.filter(a => a.type !== 'comparison').length}
            </div>
            <div className="text-sm text-slate-400">Análisis Individuales</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-amber-400">
              {analyses.length > 0 
                ? Math.round(analyses.reduce((sum, a) => sum + (a.risk_score || a.score_a || 0), 0) / analyses.length)
                : 0
              }
            </div>
            <div className="text-sm text-slate-400">Puntuación Promedio</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por tipo, rol o contenido..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              data-testid="search-history"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Type Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-colors ${
                filter === 'all' 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-slate-800/50 border border-slate-700 text-slate-400 hover:text-white'
              }`}
              data-testid="filter-all"
            >
              <BarChart3 className="w-4 h-4" />
              Todos
            </button>
            <button
              onClick={() => setFilter('analysis')}
              className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-colors ${
                filter === 'analysis' 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-slate-800/50 border border-slate-700 text-slate-400 hover:text-white'
              }`}
              data-testid="filter-analysis"
            >
              <Shield className="w-4 h-4" />
              Análisis
            </button>
            <button
              onClick={() => setFilter('comparison')}
              className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-colors ${
                filter === 'comparison' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-slate-800/50 border border-slate-700 text-slate-400 hover:text-white'
              }`}
              data-testid="filter-comparison"
            >
              <ArrowLeftRight className="w-4 h-4" />
              Comparaciones
            </button>
          </div>

          {/* Refresh */}
          <button
            onClick={loadHistory}
            disabled={loading}
            className="px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-xl text-slate-400 hover:text-white transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Timeline */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : filteredAnalyses.length === 0 ? (
          <div className="text-center py-20">
            <History className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <h3 className="text-xl font-semibold mb-2">Sin historial</h3>
            <p className="text-slate-400 mb-6">
              {searchQuery || filter !== 'all' 
                ? 'No se encontraron análisis con estos filtros'
                : 'Aún no has realizado ningún análisis de contratos'
              }
            </p>
            <div className="flex gap-4 justify-center">
              <Link
                to="/proteger-contrato"
                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-xl flex items-center gap-2"
              >
                <Shield className="w-5 h-5" />
                Proteger Contrato
              </Link>
              <Link
                to="/comparar-contratos"
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-xl flex items-center gap-2"
              >
                <ArrowLeftRight className="w-5 h-5" />
                Comparar Contratos
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedAnalyses).map(([dateLabel, items]) => (
              <div key={dateLabel}>
                {/* Date Header */}
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5 text-slate-500" />
                  <h3 className="text-lg font-semibold text-slate-300">{dateLabel}</h3>
                  <div className="flex-1 h-px bg-slate-700" />
                  <span className="text-sm text-slate-500">{items.length} análisis</span>
                </div>

                {/* Timeline Items */}
                <div className="space-y-3 pl-2 border-l-2 border-slate-700 ml-2">
                  {items.map((item, idx) => {
                    const isExpanded = expandedItem === item.id;
                    const isComparison = item.type === 'comparison';
                    
                    return (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="relative pl-6"
                      >
                        {/* Timeline Dot */}
                        <div className={`absolute left-0 top-4 w-3 h-3 -ml-[7px] rounded-full ${
                          isComparison ? 'bg-purple-500' : 'bg-emerald-500'
                        }`} />

                        {/* Card */}
                        <div className={`bg-slate-800/50 rounded-xl border overflow-hidden transition-all ${
                          isComparison ? 'border-purple-500/30' : 'border-slate-700'
                        }`}>
                          {/* Main Row */}
                          <div
                            className="p-4 cursor-pointer hover:bg-slate-800/80 transition-colors"
                            onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                          >
                            <div className="flex items-center gap-4">
                              {/* Icon */}
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isComparison ? 'bg-purple-500/20' : 'bg-emerald-500/20'
                              }`}>
                                {isComparison 
                                  ? <ArrowLeftRight className="w-5 h-5 text-purple-400" />
                                  : <Shield className="w-5 h-5 text-emerald-400" />
                                }
                              </div>

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    isComparison ? 'bg-purple-500/20 text-purple-400' : 'bg-emerald-500/20 text-emerald-400'
                                  }`}>
                                    {isComparison ? 'Comparación' : 'Análisis'}
                                  </span>
                                  {item.contract_type && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">
                                      {item.contract_type}
                                    </span>
                                  )}
                                  {item.user_role && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">
                                      {item.user_role}
                                    </span>
                                  )}
                                </div>
                                
                                {isComparison ? (
                                  <p className="text-sm text-slate-400 truncate">
                                    {item.labels?.a || 'Versión A'} vs {item.labels?.b || 'Versión B'}
                                    {item.differences_count > 0 && ` • ${item.differences_count} diferencias`}
                                  </p>
                                ) : (
                                  <p className="text-sm text-slate-400 truncate">
                                    {item.content_preview?.slice(0, 80) || 'Sin vista previa'}...
                                  </p>
                                )}
                              </div>

                              {/* Scores */}
                              <div className="flex items-center gap-3">
                                {isComparison ? (
                                  <div className="flex items-center gap-2">
                                    <div className={`px-3 py-1 rounded-lg border ${getScoreBg(item.score_a)}`}>
                                      <span className={`text-lg font-bold ${getScoreColor(item.score_a)}`}>
                                        {item.score_a}
                                      </span>
                                    </div>
                                    <span className="text-slate-500">vs</span>
                                    <div className={`px-3 py-1 rounded-lg border ${getScoreBg(item.score_b)}`}>
                                      <span className={`text-lg font-bold ${getScoreColor(item.score_b)}`}>
                                        {item.score_b}
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className={`px-3 py-1 rounded-lg border ${getScoreBg(item.risk_score)}`}>
                                    <span className={`text-lg font-bold ${getScoreColor(item.risk_score)}`}>
                                      {item.risk_score || 0}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Time */}
                              <div className="flex items-center gap-2 text-slate-500">
                                <Clock className="w-4 h-4" />
                                <span className="text-sm">{formatDate(item.created_at)}</span>
                              </div>

                              {/* Expand Icon */}
                              <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${
                                isExpanded ? 'rotate-180' : ''
                              }`} />
                            </div>
                          </div>

                          {/* Expanded Details */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="border-t border-slate-700"
                              >
                                <div className="p-4 space-y-4">
                                  {isComparison ? (
                                    <>
                                      {/* Comparison Details */}
                                      <div className="grid grid-cols-2 gap-4">
                                        <div className={`p-3 rounded-lg border ${
                                          item.preferred_contract === (item.labels?.a || 'Versión A')
                                            ? 'bg-emerald-500/10 border-emerald-500/30'
                                            : 'bg-slate-900/50 border-slate-700'
                                        }`}>
                                          <div className="flex items-center gap-2 mb-2">
                                            <FileText className="w-4 h-4 text-blue-400" />
                                            <span className="font-medium">{item.labels?.a || 'Versión A'}</span>
                                            {item.preferred_contract === (item.labels?.a || 'Versión A') && (
                                              <Award className="w-4 h-4 text-amber-400" />
                                            )}
                                          </div>
                                          <p className="text-xs text-slate-400 line-clamp-2">
                                            {item.contract_a_preview || 'Sin vista previa'}
                                          </p>
                                        </div>
                                        <div className={`p-3 rounded-lg border ${
                                          item.preferred_contract === (item.labels?.b || 'Versión B')
                                            ? 'bg-emerald-500/10 border-emerald-500/30'
                                            : 'bg-slate-900/50 border-slate-700'
                                        }`}>
                                          <div className="flex items-center gap-2 mb-2">
                                            <FileText className="w-4 h-4 text-purple-400" />
                                            <span className="font-medium">{item.labels?.b || 'Versión B'}</span>
                                            {item.preferred_contract === (item.labels?.b || 'Versión B') && (
                                              <Award className="w-4 h-4 text-amber-400" />
                                            )}
                                          </div>
                                          <p className="text-xs text-slate-400 line-clamp-2">
                                            {item.contract_b_preview || 'Sin vista previa'}
                                          </p>
                                        </div>
                                      </div>

                                      {item.preferred_contract && (
                                        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                                          <Award className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                                          <div>
                                            <span className="font-medium text-amber-300">Recomendación: </span>
                                            <span className="text-amber-200">{item.preferred_contract}</span>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {/* Analysis Details */}
                                      <div className="flex items-center gap-4">
                                        {item.risk_level && (
                                          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                                            getRiskLevelConfig(item.risk_level).bg
                                          }`}>
                                            {React.createElement(getRiskLevelConfig(item.risk_level).icon, {
                                              className: `w-4 h-4 ${getRiskLevelConfig(item.risk_level).text}`
                                            })}
                                            <span className={getRiskLevelConfig(item.risk_level).text}>
                                              Riesgo {item.risk_level}
                                            </span>
                                          </div>
                                        )}
                                        {item.clauses_suggested > 0 && (
                                          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/20">
                                            <Target className="w-4 h-4 text-blue-400" />
                                            <span className="text-blue-400">{item.clauses_suggested} cláusulas sugeridas</span>
                                          </div>
                                        )}
                                      </div>

                                      {item.content_preview && (
                                        <div className="p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
                                          <p className="text-sm text-slate-400">{item.content_preview}</p>
                                        </div>
                                      )}
                                    </>
                                  )}

                                  {/* Actions */}
                                  <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                                    <span className="text-xs text-slate-500">
                                      ID: {item.id?.slice(0, 8)}...
                                    </span>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); downloadPDF(item.id, item.type); }}
                                        disabled={downloading === item.id}
                                        className="px-3 py-1.5 text-sm text-emerald-400 hover:bg-emerald-500/20 rounded-lg flex items-center gap-1.5 transition-colors"
                                        data-testid={`download-pdf-${item.id}`}
                                      >
                                        {downloading === item.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Download className="w-4 h-4" />
                                        )}
                                        Descargar PDF
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); deleteAnalysis(item.id); }}
                                        disabled={deleting === item.id}
                                        className="px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/20 rounded-lg flex items-center gap-1.5 transition-colors"
                                      >
                                        {deleting === item.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Trash2 className="w-4 h-4" />
                                        )}
                                        Eliminar
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ProtectionHistoryPage;
